import type { AlertSectionLink } from '@/components/common/AlertBanner'

/**
 * Stable section anchor ids for the parent-relationship rows in the validation
 * banner. Derived from the parent's `relationship` rather than tied to a fixed
 * mother/father slot, so the rest of the validation/banner pipeline stays
 * list-driven (Phase P8 of the MOTHER/FATHER legacy migration). Kept as
 * literal strings so existing analytics anchors don't shift.
 */
export type ParentSectionAnchorId = 'biological-mother' | 'biological-father'

/**
 * Map a parent comparator's `relationship` to the stable banner-section anchor
 * id and human-readable name. Centralized so the validation banner, the
 * fix-me link builder, and any future consumer all derive the same ids/labels
 * from the same source of truth (the relationship).
 */
export function parentSectionAnchorIdForRelationship(relationship: 'MOTHER' | 'FATHER'): ParentSectionAnchorId {
  return relationship === 'MOTHER' ? 'biological-mother' : 'biological-father'
}

export function parentSectionLabelForRelationship(relationship: 'MOTHER' | 'FATHER'): string {
  return relationship === 'MOTHER' ? 'Biological Mother' : 'Biological Father'
}

/**
 * One parent row's input to {@link buildValidationFailureSectionLinks}: the
 * relationship that drives id/label derivation, plus the
 * already-computed selection + attention flags and the editor opener.
 *
 * The caller passes the array in its desired display order (typically
 * MOTHER → FATHER for the Send Updates banner); the builder preserves it.
 */
export interface ValidationFailureParentSection {
  relationship: 'MOTHER' | 'FATHER'
  /** Whether this parent is selected on the order (sequenced or sample-only). */
  selected: boolean
  /**
   * Whether this parent's section currently fails validation (incomplete
   * comparator data and/or a still-needed research-contact channel). Combined
   * with `selected` so the caller doesn't need to encode the same precedence
   * here.
   */
  needsAttention: boolean
  /** Open the comparator editor for this parent. */
  onEdit: () => void
}

/**
 * A non-parent comparator row (sibling / extended relative) that is currently
 * incomplete and blocking send. Parents are surfaced via `parentSections`
 * (relationship-derived ids/labels); these carry their own id/label so the
 * banner can link straight to the specific comparator's editor.
 */
export interface ValidationFailureComparatorSection {
  /** Stable anchor/link id (e.g. the comparator's client id). */
  id: string
  /** Human-readable label for the link (e.g. "Sibling"). */
  name: string
  /** Open the editor for this comparator. */
  onEdit: () => void
}

/**
 * Inputs for {@link buildValidationFailureSectionLinks}. The parent rows are
 * passed as an explicit list keyed by `relationship` so the banner is no
 * longer hardcoded to a mother/father slot pair; adding another parent (or
 * dropping one) is a list change here, not a new boolean parameter.
 */
export interface ValidationFailureSectionLinkParams {
  /** Parent rows in display order (preserved verbatim by the builder). */
  parentSections: ValidationFailureParentSection[]
  /**
   * UX-054: incomplete non-parent comparators (sibling / extended relative)
   * added or edited on the submitted order, in display order. Surfaced after
   * the parent rows so a blank generic comparator can't silently pass the send
   * gate. Parents remain in `parentSections`.
   */
  comparatorSections?: ValidationFailureComparatorSection[]
  guardianStandaloneResearchAttention: boolean
  onEditGuardian: () => void
  /**
   * Whether the (autosaved) clinical summary carries characters outside the
   * server free-text allowlist. Autosave persists as the user types, so an
   * invalid character blocks sending and must surface as a fix-me link rather
   * than silently disabling "Send Updates" while the banner still says the
   * updates are ready.
   */
  clinicalSummaryHasInvalidChars?: boolean
  /**
   * UX-054: whether a *changed* clinical summary is now empty while the org
   * requires it (the erase-to-empty case). Distinct from
   * `clinicalSummaryHasInvalidChars` but surfaces the same fix-me link.
   */
  clinicalSummaryIncomplete?: boolean
  /** Open the merged Clinical Indications for Testing editor. Required when the clinical-summary or phenotype flags can be true. */
  onEditClinicalSummary?: () => void
  /** UX-054: whether required phenotypes (indications) are missing after a change. */
  indicationsIncomplete?: boolean
  /** UX-054: whether required family history is missing after a change. */
  familyHistoryIncomplete?: boolean
  /** Open the Family History editor. Required when `familyHistoryIncomplete` can be true. */
  onEditFamilyHistory?: () => void
  /**
   * Whether the proband "Additional Options" (secondary consent) block was
   * changed and is now incomplete — incidental left unanswered, or a
   * `requireInteraction` NY / research consent left unanswered.
   */
  additionalOptionsIncomplete?: boolean
  /** Open the proband Additional Options / consent editor. Required when `additionalOptionsIncomplete` can be true. */
  onEditAdditionalOptions?: () => void
}

/**
 * Build the list of sections that currently fail validation while the user is
 * editing an in-progress order. Each selected-and-attention parent row
 * surfaces a link with a relationship-derived id + label; incomplete non-parent
 * comparators surface next; the clinical summary surfaces a link when it
 * contains disallowed characters OR is required-but-empty; indications and
 * family history surface when required-but-empty; the guardian surfaces when it
 * needs standalone research attention. Order follows the input `parentSections`
 * array (typically MOTHER → FATHER), then comparators, clinical summary,
 * indications, family history, then the guardian.
 */
export function buildValidationFailureSectionLinks(params: ValidationFailureSectionLinkParams): AlertSectionLink[] {
  const links: AlertSectionLink[] = []

  for (const parent of params.parentSections) {
    if (parent.selected && parent.needsAttention) {
      links.push({
        id: parentSectionAnchorIdForRelationship(parent.relationship),
        name: parentSectionLabelForRelationship(parent.relationship),
        onClick: parent.onEdit,
      })
    }
  }

  for (const comparator of params.comparatorSections ?? []) {
    links.push({ id: comparator.id, name: comparator.name, onClick: comparator.onEdit })
  }

  // Both the summary text and the phenotypes are edited in one modal now, so the
  // two conditions share a single fix-me link rather than offering the clinician
  // two names for the same destination.
  if (
    (params.clinicalSummaryHasInvalidChars || params.clinicalSummaryIncomplete || params.indicationsIncomplete) &&
    params.onEditClinicalSummary
  ) {
    links.push({
      id: 'clinical-summary',
      name: 'Clinical Indications for Testing',
      onClick: params.onEditClinicalSummary,
    })
  }

  if (params.familyHistoryIncomplete && params.onEditFamilyHistory) {
    links.push({ id: 'family-history', name: 'Family History', onClick: params.onEditFamilyHistory })
  }

  if (params.additionalOptionsIncomplete && params.onEditAdditionalOptions) {
    links.push({ id: 'additional-options', name: 'Additional Options', onClick: params.onEditAdditionalOptions })
  }

  if (params.guardianStandaloneResearchAttention) {
    links.push({ id: 'guardian-contact', name: 'Guardian Contact', onClick: params.onEditGuardian })
  }

  return links
}
